/**
 * Utility for persisting and restoring advanced search filter state
 * for the Search Projects page (ubt/search-project/projects API).
 *
 * Stored under the session key "ubt-advance-search", separate from the
 * shared basic-filter key "ubt-shared-search".
 */

const ADVANCE_SEARCH_SESSION_KEY = "ubt-advance-search";

/** Shape of a multi-select dropdown's selected state. */
export interface MultiSelectState {
  isAllSelected: boolean;
  selectedIds: (string | number)[];
}

/** All fields persisted for the advanced search panel. */
export interface AdvanceSearchData {
  /** Focus Area multi-select selection (maps to selectedRating in Redux). */
  selectedFocusArea?: MultiSelectState;
  /** Value Compass multi-select selection. */
  selectedValueCompass?: MultiSelectState;
  /** Metrics multi-select selection (maps to selectedMetrix in Redux). */
  selectedMetrics?: MultiSelectState;
  /** Results multi-select selection. */
  selectedResults?: MultiSelectState;
  /** Test of Change Rating multi-select selection. */
  selectedTestOfChangeRating?: MultiSelectState;
  /** Project type radio value: "Active" | "Complete" | "Both". */
  selectedProjectType?: string;
  /** Range Start Activity date in MM-DD-YYYY display format. */
  rangeStartDate?: string;
  /** Range End Activity date in MM-DD-YYYY display format. */
  rangeEndDate?: string;
  /** Minimum ROI filter value. */
  minROI?: string;
  /** Maximum ROI filter value. */
  maxROI?: string;
  /** Epoch timestamp of when the data was saved. */
  timestamp?: number;
}

/**
 * Saves advanced search filter data to sessionStorage.
 * Silently swallows errors (e.g. private-browsing quota limits).
 */
export const saveAdvanceSearch = (data: AdvanceSearchData): void => {
  try {
    const payload: AdvanceSearchData = {
      ...data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(
      ADVANCE_SEARCH_SESSION_KEY,
      JSON.stringify(payload),
    );
  } catch (error) {
    console.error(
      "Failed to save advance search data to sessionStorage:",
      error,
    );
  }
};

/**
 * Loads advanced search filter data from sessionStorage.
 * Returns null when no data is stored or if parsing fails.
 */
export const loadAdvanceSearch = (): AdvanceSearchData | null => {
  try {
    const stored = sessionStorage.getItem(ADVANCE_SEARCH_SESSION_KEY);
    if (!stored) return null;

    const data: AdvanceSearchData = JSON.parse(stored);
    if (!data || typeof data !== "object") return null;

    return data;
  } catch (error) {
    console.error(
      "Failed to load advance search data from sessionStorage:",
      error,
    );
    return null;
  }
};

/**
 * Removes the advanced search data from sessionStorage.
 */
export const clearAdvanceSearch = (): void => {
  try {
    sessionStorage.removeItem(ADVANCE_SEARCH_SESSION_KEY);
  } catch (error) {
    console.error(
      "Failed to clear advance search data from sessionStorage:",
      error,
    );
  }
};

/**
 * Returns true when there is stored advanced search data.
 */
export const hasStoredAdvanceSearch = (): boolean => {
  try {
    return sessionStorage.getItem(ADVANCE_SEARCH_SESSION_KEY) !== null;
  } catch {
    return false;
  }
};

/**
 * Converts a multi-select state object to the tilde-delimited string
 * expected by the search API (e.g. "1~2~3" or "All").
 */
export const formatMultiSelectForApi = (
  selected: MultiSelectState | undefined,
): string => {
  if (!selected || selected.isAllSelected) return "All";
  return selected.selectedIds.length > 0
    ? selected.selectedIds.join("~")
    : "All";
};
